import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { CameraGlyph, SearchGlyph } from "./userui";
import { applyTemplate } from "./privileges/privilegeModel";

/* The Add user and Edit user sheet (user.css .bmuf): a header with the photo,
   a Details tab for the form and a Privileges tab (Teja, 10-28). Layout
   only - each modal keeps its own state, checks and calls, and hands them in.

   Privileges revamp (owner 11-03): the groups come in ready-made
   (privileges/privilegeModel.sheetGroups): the v2 catalog's areas with plain
   labels and descriptions plus a read-only "Retired" group, or - when v2 did
   not answer - the v1 rows by page as before. Picking a Role never changes the
   ticks any more: "Apply <role> template" shows what it would add / remove
   first, and only "Use these ticks" changes them. */

// The pages the privilege list has always shown, in the order shown (the
// fallback grouping when the v2 catalog is not available).
export const PRIV_PAGES = [
  { key: "Home Screen", label: "Home screen" },
  { key: "Tasks", label: "Tasks" },
  { key: "Side Bar", label: "Side bar" },
  { key: "RPM", label: "RPM" },
  { key: "Payment", label: "Payment" },
  { key: "QA", label: "QA" },
];

// Privilege names that don't match the UI they control - show the
// recognizable label to admins while the stored gate string is unchanged.
const PRIVILEGE_LABELS = { Threads: "Non-Visit Notes" };
export const privLabel = (name) => PRIVILEGE_LABELS[name] || name;

const UserGlyph = (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4.5 20.5c1.2-3.6 4-5.5 7.5-5.5s6.3 1.9 7.5 5.5" />
  </svg>
);
const ShieldGlyph = (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.3 7.5 9.5 4.4-1.2 7.5-4.9 7.5-9.5V6z" />
    <polyline points="8.8 12.2 11.2 14.6 15.4 10" />
  </svg>
);

const labelsOf = (groups, ids) => {
  const all = groups.flatMap((g) => g.items);
  return ids.map((id) => (all.find((p) => p.id === String(id)) || { label: `#${id}` }).label).join(", ");
};

/* "Apply <role> template": mode pick, then a preview, then Use these ticks. */
export const TemplateApply = ({ groups, roleName, template, selected, setSelected, keepIds }) => {
  const [mode, setMode] = useState("add");
  const [preview, setPreview] = useState(null);
  if (!template) return null;
  const editableIds = groups.filter((g) => g.editable).flatMap((g) => g.items.map((p) => p.id));
  const run = (m) => setPreview(applyTemplate({ template, current: selected, editableIds, keepIds, mode: m }));
  return (
    <div className="bmuf-tpl" data-testid="role-template">
      <div className="bmuf-tpl-row">
        <select aria-label="Template mode" value={mode}
                onChange={(e) => { setMode(e.target.value); if (preview) run(e.target.value); }}>
          <option value="add">Add its privileges</option>
          <option value="replace">Match it exactly</option>
        </select>
        <button type="button" className="bmuf-link" onClick={() => run(mode)}>
          Apply {roleName} template
        </button>
      </div>
      {preview && (
        <div className="bmuf-tpl-prev" data-testid="template-preview">
          {preview.add.length === 0 && preview.remove.length === 0 && <p>The {roleName} template would change nothing.</p>}
          {preview.add.length > 0 && <p className="add">Adds: {labelsOf(groups, preview.add)}</p>}
          {preview.remove.length > 0 && <p className="rem">Removes: {labelsOf(groups, preview.remove)}</p>}
          {template.length === 0 && <p>This role's template is empty.</p>}
          <div className="bmuf-tpl-row">
            <button type="button" className="bmuf-save sm" onClick={() => { setSelected(preview.next); setPreview(null); }}>
              Use these ticks
            </button>
            <button type="button" className="bmuf-cancel sm" onClick={() => setPreview(null)}>Cancel</button>
            <span className="bmuf-tpl-note">Nothing is saved until you save the user.</span>
          </div>
        </div>
      )}
    </div>
  );
};

const PrivilegePanel = ({ groups, sel, held, onToggle, setSelected, railNote, readOnly, note, template }) => {
  const [groupKey, setGroupKey] = useState(groups[0]?.key);
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const group = groups.find((g) => g.key === groupKey) || groups[0];
  const isOn = (p) => (p.editable ? sel.has(p.id) : held.has(p.id));
  const items = q
    ? groups.flatMap((g) => g.items.map((p) => ({ ...p, group: g.label })))
        .filter((p) => `${p.label} ${p.description || ""}`.toLowerCase().includes(q))
    : (group?.items || []);
  const editableShown = items.filter((p) => p.editable);
  const onCount = items.filter(isOn).length;
  const ids = editableShown.map((p) => p.id);
  const allOn = ids.length > 0 && ids.every((id) => sel.has(id));
  const setAll = (turnOn) =>
    setSelected((prev) => {
      const rest = prev.map(String).filter((id) => !ids.includes(id));
      return turnOn ? [...rest, ...ids] : rest;
    });

  return (
    <div className="bmuf-pv">
      <nav className="bmuf-rail" aria-label="Privilege groups">
        <div className="bmuf-rail-list">
          {groups.map((g) => {
            const n = g.items.filter(isOn).length;
            const active = !q && g.key === group?.key;
            return (
              <button
                key={g.key}
                type="button"
                className={`bmuf-rail-i${active ? " on" : ""}${g.editable ? "" : " ro"}`}
                aria-current={active ? "true" : undefined}
                onClick={() => {
                  setGroupKey(g.key);
                  setQuery("");
                }}
              >
                <span>{g.label}</span>
                <span className={`bmuf-n${g.editable && n === g.items.length ? " full" : ""}`}>
                  {n}/{g.items.length}
                </span>
              </button>
            );
          })}
        </div>
        <p className="bmuf-rail-note">{railNote}</p>
      </nav>

      <div className="bmuf-pane">
        <label className="bmuf-psearch">
          {SearchGlyph}
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search privileges"
            aria-label="Search privileges"
          />
        </label>
        {note && <p className="bmuf-pnote">{note}</p>}
        {!readOnly && template}

        <div className="bmuf-pane-head">
          <h3>
            {q ? "Matching privileges" : `${group?.label} privileges`}
            <span>
              {onCount} of {items.length} on
            </span>
          </h3>
          {!readOnly && ids.length > 0 && (
            <button type="button" className="bmuf-link" onClick={() => setAll(!allOn)}>
              {allOn ? "Clear all" : "Select all"}
            </button>
          )}
        </div>
        {!q && group && !group.editable && (
          <p className="bmuf-pnote">No screen checks these any more. They stay on the user exactly as they are and cannot be changed here.</p>
        )}

        {items.length === 0 ? (
          <p className="bmuf-none">No privileges match &ldquo;{query}&rdquo;.</p>
        ) : (
          <div className="bmuf-cards">
            {items.map((p) => (
              <label className={`bmuf-card${p.editable ? "" : " ro"}`} key={p.id} htmlFor={`privilege-${p.id}`}>
                <input
                  type="checkbox"
                  id={`privilege-${p.id}`}
                  value={p.id}
                  aria-label={p.label}
                  onChange={onToggle}
                  checked={isOn(p)}
                  disabled={readOnly || !p.editable}
                />
                <span>
                  {p.label}
                  {p.description && <small>{p.description}</small>}
                </span>
                {q && <em>{p.group}</em>}
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const UserSheet = ({
  title,
  sub,
  initials,
  photo,
  onPhoto,
  onClose,
  error,
  details,
  groups = [],
  held = [],
  selected,
  setSelected,
  onToggle,
  roleName,
  template = null,
  keepIds = [],
  privError,
  privNote,
  readOnly = false,
  banner,
  footer,
  children,
}) => {
  const [tab, setTab] = useState("details");
  const [photoBroken, setPhotoBroken] = useState(false);

  const sel = new Set((selected || []).map(String));
  const heldSet = new Set((held || []).map(String));
  const editable = groups.filter((g) => g.editable).flatMap((g) => g.items);
  const on = editable.filter((p) => sel.has(p.id)).length;
  const total = editable.length;
  const defSet = template ? new Set(template.map(String)) : null;
  const changed = defSet ? editable.filter((p) => sel.has(p.id) !== defSet.has(p.id)).length : null;

  const railNote = !defSet
    ? "No role picked yet"
    : changed === 0
      ? `Matches the ${roleName} template`
      : `${changed} differ from the ${roleName} template`;
  const templateUi = !readOnly && defSet ? (
    <TemplateApply groups={groups} roleName={roleName} template={template} selected={(selected || []).map(String)}
                   setSelected={setSelected} keepIds={keepIds} />
  ) : null;
  const errors = Array.isArray(error) ? error.filter(Boolean) : error ? [error] : [];

  return (
    <div className="bmuf-layer">
      <div className="bmuf" role="dialog" aria-modal="true" aria-labelledby="bmuf-title">
        <div className="bmuf-head">
          <label className="bmuf-ava" title="Change photo">
            <span className="bmuf-ava-txt">{initials || "?"}</span>
            {photo && !photoBroken && <img src={photo} alt="" onError={() => setPhotoBroken(true)} />}
            <input
              type="file"
              accept="image/*"
              aria-label="Profile photo"
              onChange={(e) => {
                setPhotoBroken(false);
                onPhoto(e);
              }}
            />
            <span className="bmuf-ava-cam" aria-hidden="true">{CameraGlyph}</span>
          </label>
          <div className="bmuf-head-text">
            <h2 id="bmuf-title">{title}</h2>
            {sub && <p>{sub}</p>}
          </div>
          <button type="button" className="bmuf-x" onClick={onClose} aria-label="Close" title="Close">
            <CloseIcon style={{ fontSize: 21 }} />
          </button>
        </div>
        {banner && <div className="bmuf-banner" data-testid="sheet-banner">{banner}</div>}

        <div className="bmuf-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "details"}
            className={`bmuf-tab${tab === "details" ? " on" : ""}`}
            onClick={() => setTab("details")}
          >
            {UserGlyph} Details
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "privileges"}
            className={`bmuf-tab${tab === "privileges" ? " on" : ""}${privError ? " bad" : ""}`}
            onClick={() => setTab("privileges")}
          >
            {ShieldGlyph} Privileges
            <span className="bmuf-tab-n">
              {on}/{total}
            </span>
          </button>
        </div>

        {tab === "details" ? (
          <div className="bmuf-body">
            {details}
            <div className={`bmuf-sum${privError ? " bad" : ""}`}>
              {ShieldGlyph}
              <p>
                <b>
                  {on} of {total} privileges
                </b>{" "}
                {!defSet
                  ? "on. Pick a role to compare with its template."
                  : changed === 0
                    ? `match the ${roleName} template.`
                    : `on, ${changed} differ from the ${roleName} template.`}
                {privError && <span className="msg">{privError}</span>}
              </p>
              <button type="button" className="bmuf-link" onClick={() => setTab("privileges")}>
                Review privileges
              </button>
            </div>
            {templateUi}
          </div>
        ) : (
          <PrivilegePanel
            groups={groups}
            sel={sel}
            held={heldSet}
            onToggle={onToggle}
            setSelected={setSelected}
            railNote={railNote}
            readOnly={readOnly}
            note={privNote}
            template={templateUi}
          />
        )}

        {errors.length > 0 && (
          <div className="bmuf-errs" role="alert" data-testid="sheet-errors">
            {errors.map((m) => <p key={m} className="bmuf-err">{m}</p>)}
          </div>
        )}
        <div className="bmuf-foot">{footer}</div>
      </div>
      {children}
    </div>
  );
};

export default UserSheet;
